import { Changeset } from 'ember-changeset'
import { capitalize } from '@ember/string'
import { csvTeacherValidator } from 'teacher-ui-client/teacher/validator'
import { setProperties } from '@ember/object'
import { isConflictError } from 'teacher-ui-client/resource-conflict/error'
import { task } from 'ember-concurrency'
import type { BufferedChangeset } from 'ember-changeset/types'
import type { IntlService } from 'ember-intl'
import lookupValidator from 'teacher-ui-client/utils/lookup-validator'
import type ResourceConflictService from 'teacher-ui-client/services/resource-conflict'
import Service, { service } from '@ember/service'
import type SessionService from 'teacher-ui-client/services/session'
import type Store from '@ember-data/store'
import type Teacher from 'teacher-ui-client/models/teacher'
import { zipObject } from 'lodash'
import type { Log } from '@blakeelearning/log'
import RSVP from 'rsvp'
import { parse, type ParseResult } from 'papaparse'
import { tracked } from '@glimmer/tracking'
import type Model from '@ember-data/model'

interface RowError {
  source?: {
    row: number
    pointer: string
  }
  meta?: {
    conflict?: {
      type: string
    }
  }
}

function markWithError(teacherChangeset: BufferedChangeset, row: number, errors: RowError[]) {
  errors.forEach((error) => {
    if (error?.source?.row === row) {
      const attr = error?.source?.pointer.replace(/^.*\//, '')
      const name = capitalize(attr)
      if (error?.meta?.conflict?.type) {
        const resource = capitalize(error.meta.conflict.type)
        teacherChangeset.pushErrors(attr, `${name} is already taken by a ${resource}`)
      } else {
        teacherChangeset.pushErrors(attr, `${name} is already taken`)
      }
    }
  })
}

export type ChangesetFor<T extends Model> = BufferedChangeset & { data: T }

export interface TeacherWithCsvMeta extends Teacher {
  rowIndex: number
  trialIndicator: string
}

export default class TeacherService extends Service {
  @service store!: Store

  @service session!: SessionService

  @service intl!: IntlService

  @service resourceConflict!: ResourceConflictService

  @service log!: Log

  @tracked file: Nullable<File> = null

  cleanupImport = () => {
    this.file = null
    // this may get called in destroy hooks, so make sure to check
    if (this.store.isDestroyed || this.store.isDestroying) return
    this.store
      .peekAll<Teacher>('teacher')
      .filter((teacher) => teacher.isNew)
      .forEach((teacher) => {
        teacher.rollbackAttributes()
      })
  }

  importFromCsv = task({ restartable: true }, async (subscriptionType, file) => {
    this.cleanupImport()

    const { school } = this.session.currentTeacher

    let rowIndex = 0

    this.file = file

    const existingTeachers = this.store.peekAll<Teacher>('teacher').filter((teacher) => !teacher.isNew)
    const newTeachers: ChangesetFor<TeacherWithCsvMeta>[] = await new RSVP.Promise((resolve) => {
      let changesets: ChangesetFor<TeacherWithCsvMeta>[] = []
      // A function to run for each line in the file
      const step = (result: ParseResult<unknown>) => {
        // for each line in the file, convert it to a teacher changeset
        const inputProperties = zipObject(['firstName', 'lastName', 'email', 'trialIndicator'], result.data)

        const model = this.store.createRecord<Teacher>('teacher', { school })
        const changeset = Changeset(
          model,
          lookupValidator(csvTeacherValidator, { intl: this.intl }),
          csvTeacherValidator,
        ) as ChangesetFor<TeacherWithCsvMeta>

        setProperties(changeset, inputProperties)

        const email = changeset.get('email') as unknown
        const trialIndicator = changeset.get('trialIndicator') as unknown
        const isTrial = trialIndicator === 'Y'
        const accountType = isTrial ? 'teacher-subscription' : 'school-subscription'

        setProperties(changeset, {
          login: email,
          accountType,
          subscriptionType,
          parseResult: result,
          rowIndex: (rowIndex += 1),
        })

        changesets = [...changesets, changeset]
      }

      const complete = () => resolve(changesets)

      parse(file, {
        complete,
        step,
        skipEmptyLines: true,
      })
    })

    // check for duplicate emails with new teachers.
    newTeachers.forEach((teacherChangeset) => {
      // if 2+ new teachers have this email address, its a duplicate
      const duplicateEmailNew = newTeachers.filter((t) => t.email === teacherChangeset.email).length >= 2
      // if any 1 existing teacher has this email address, its a duplicate
      const duplicateEmailExisting = existingTeachers.filter((t) => t.email === teacherChangeset.email).length >= 1
      if (duplicateEmailNew || duplicateEmailExisting) {
        teacherChangeset.pushErrors('email', 'Email is already taken')
      }
    })

    try {
      if (newTeachers.every((newTeacher) => newTeacher.isValid)) {
        const savePromises = newTeachers.map((newTeacher) =>
          newTeacher.save({
            adapterOptions: {
              bulk: true,
              include: ['school-classes', 'teacher-subscriptions'].join(','),
              accountType: newTeacher.accountType,
              subscriptionType,
            },
          }),
        )
        await Promise.all(savePromises)
      }
      return newTeachers
    } catch (error: any) {
      if (Array.isArray(error?.errors)) {
        // Looks like conflict errors (409) are only returned by backend during rollover to prevent any teacher operations
        // happening in parallel.
        const conflictError = error?.errors.find(isConflictError)
        if (conflictError) {
          this.resourceConflict.handleError('Conflict Error', conflictError)
          return Promise.reject(new Error('conflict error'))
        }

        // All the 422s here are usually resource conflict errors when the teacher's email has already been taken.
        const invalidError = error?.errors.find((err: any) => err?.status === '422')
        if (invalidError) {
          newTeachers.forEach((teacher, row) => markWithError(teacher, row, error?.errors))
          return newTeachers
        }
      }

      this.log.error('unknown failure uploading teacher csv', error)
      return Promise.reject(new Error('unknown failure uploading teacher csv'))
    }
  })
}

declare module '@ember/service' {
  interface Registry {
    teacher: TeacherService
  }
}
